import React, { forwardRef, useImperativeHandle, useState, useEffect, useContext, useRef } from "react";
import { TextField, DetailsList, Label, SelectionMode, Text } from "@fluentui/react";
import { getSLEntryReviewers } from "./SLCreateRequests";
import LoadingSpinner from "../../baseComponents/LoadingSpinner";
import { UserContext } from "../../contexts/UserContext";
import { SLCreateSLBatchContext } from "../../contexts/SLCreateSLBatchContext";
import { SLLoadingStatus } from "../../utilities/utilities";
import { ISLEntryResponse } from "./SLCreateSchemas";

const columns = [
  {
    key: "col1",
    name: "Alias",
    fieldName: "alias",
    minWidth: 100,
    maxWidth: 200,
    isResizable: true
  },
  {
    key: "col2",
    name: "Full Name",
    fieldName: "fullName",
    minWidth: 100,
    maxWidth: 200,
    isResizable: true
  }
];

export interface ISLEntryReviewers {
  alias: string;
  fullName: string;
  personnelNum: string;
}

function parseReviewersResponse(result: any): ISLEntryReviewers[] {
  const reviewersList = result.data;

  return reviewersList.map((item: any) => {
    return {
      alias: item.alias,
      fullName: item.fullName,
      personnelNum: item.personnelNum
    } as ISLEntryReviewers;
  });
}

export interface SLCreateReviewersRef {
  getReviewers: () => ISLEntryReviewers[];
}

const SLCreateReviewers = forwardRef((props, ref: React.Ref<SLCreateReviewersRef>) => {
  const { accessToken } = useContext(UserContext);
  const [reviewers, setReviewers] = useState<ISLEntryReviewers[]>([]);
  const [loadingStatus, setLoadingStatus] = useState<SLLoadingStatus>(SLLoadingStatus.Idle);

  useImperativeHandle(ref, () => ({
    getReviewers() {
      return reviewers;
    }
  }));

  useEffect(() => {
    let mounted = true;
    if (loadingStatus !== SLLoadingStatus.Pending) {
      setLoadingStatus(SLLoadingStatus.Pending);
      const loadReviews = async () => {
        try {
          const resultsPromise = getSLEntryReviewers(accessToken);
          const reviewers = parseReviewersResponse(await resultsPromise);
          if (mounted) {
            setReviewers(reviewers);
            setLoadingStatus(SLLoadingStatus.Resolved);
          }
        } catch (e) {
          if (mounted) {
            setLoadingStatus(SLLoadingStatus.Rejected);
          }
        }
      };
      loadReviews();
    }
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadingStatus === SLLoadingStatus.Pending) {
    return <LoadingSpinner label="Loading reviewers" />;
  } else if (loadingStatus === SLLoadingStatus.Idle) {
    return null;
  } else if (loadingStatus === SLLoadingStatus.Rejected) {
    return (
      <Text
        variant="xLarge"
        style={{
          color: "var(--accent-font-color, gray)",
          textAlign: "center"
        }}
      >
        Error while loading Reviewers.
      </Text>
    );
  }

  return (
    <>
      <Label>Reviewers</Label>
      <DetailsList
        compact={true}
        items={reviewers}
        columns={columns}
        selectionMode={SelectionMode.none}
        ariaLabel={"Reviewer Grid"}
        ariaLabelForGrid={"Table for displaying reviewers"}
      />
    </>
  );
});

SLCreateReviewers.displayName = "SLCreateReviewers";

export interface SLCreatePostersRef {
  getAdditionalPosters: () => string;
  setAdditionalPosters: (additionalPosters: string) => void;
  reset: () => void;
}

const SLCreatePosters = forwardRef((props, ref: React.Ref<SLCreatePostersRef>) => {
  const entryContext = useContext(SLCreateSLBatchContext);
  const [loadingStatus, setLoadingStatus] = useState<SLLoadingStatus>(SLLoadingStatus.Idle);
  const [additionalPosters, setAdditionalPosters] = useState("");

  useImperativeHandle(ref, () => ({
    getAdditionalPosters() {
      return additionalPosters;
    },
    setAdditionalPosters(additionalPosters: string) {
      setAdditionalPosters(additionalPosters);
    },
    reset() {
      setAdditionalPosters("");
    }
  }));

  useEffect(() => {
    if (entryContext.loadingStatus === SLLoadingStatus.Resolved) {
      if (entryContext.entry) {
        const entry = entryContext.entry as ISLEntryResponse;
        setAdditionalPosters(entry.additionalPoster || "");
      } else {
        setAdditionalPosters("");
      }
      setLoadingStatus(SLLoadingStatus.Resolved);
    } else if (entryContext.loadingStatus === SLLoadingStatus.Rejected) {
      setLoadingStatus(SLLoadingStatus.Rejected);
    }
  }, [entryContext.entry, entryContext.loadingStatus]);

  if (loadingStatus === SLLoadingStatus.Pending) {
    return <LoadingSpinner label="Loading posters" />;
  } else if (loadingStatus === SLLoadingStatus.Idle) {
    return null;
  } else if (loadingStatus === SLLoadingStatus.Rejected) {
    return (
      <Text
        variant="xLarge"
        style={{
          color: "var(--accent-font-color, gray)",
          textAlign: "center"
        }}
      >
        Error while loading Additional Posters.
      </Text>
    );
  }

  return (
    <>
      <Label>Posters</Label>
      <TextField
        name="postersText"
        ariaLabel="Posters for Entry"
        multiline
        value={additionalPosters}
        onChange={(_, text) => setAdditionalPosters(text || "")}
        autoAdjustHeight
        rows={3}
        disabled={entryContext.isReadOnly}
        description="Please enter poster aliases separated by semi-colon( ; )"
      />
    </>
  );
});

SLCreatePosters.displayName = "SLCreatePosters";

export interface ISLCreatePostersReviewersRef {
  getReviewers: () => ISLEntryReviewers[];
  getAdditionalPosters: () => string;
  setAdditionalPosters: (additionalPosters: string) => void;
  reset: () => void;
}

const SLCreatePostersReviewers = forwardRef((props, ref: React.Ref<ISLCreatePostersReviewersRef>) => {
  const reviewersRef = useRef<SLCreateReviewersRef>(null);
  const postersRef = useRef<SLCreatePostersRef>(null);

  useImperativeHandle(ref, () => ({
    getAdditionalPosters() {
      if (postersRef.current) {
        return postersRef.current.getAdditionalPosters();
      }
      return "";
    },
    getReviewers() {
      if (reviewersRef.current) {
        return reviewersRef.current.getReviewers();
      }
      return [];
    },
    setAdditionalPosters(additionalPosters: string) {
      if (postersRef.current) {
        postersRef.current.setAdditionalPosters(additionalPosters);
      }
    },
    reset() {
      if (postersRef.current) {
        postersRef.current.reset();
      }
    }
  }));

  return (
    <React.Fragment>
      <div className="ms-Grid" dir="ltr">
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-lg6">
            <SLCreatePosters ref={postersRef} />
          </div>
          <div className="ms-Grid-col ms-lg6">
            <SLCreateReviewers ref={reviewersRef} />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
});

SLCreatePostersReviewers.displayName = "SLCreatePostersReviewers";

export default SLCreatePostersReviewers;
